import React, { useEffect, useState } from 'react';
import { Plus, Loader2, Heart, Target, Users, Trash2, Pencil, Layers } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { canEditEntry, isElevatedOrAdmin } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import DatePicker from '@/components/DatePicker';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0);
const TRANCHE_STATUS = { prevue: { label: 'Prévue', cls: 'bg-surface-muted text-foreground/70' }, recue: { label: 'Reçue', cls: 'bg-primary/15 text-primary' }, retard: { label: 'En retard', cls: 'bg-destructive/15 text-destructive' } };

export default function Grants() {
    const { t } = useLang();
    const { user, profile, loading: authLoading } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [donors, setDonors] = useState([]);
    const [grants, setGrants] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [projects, setProjects] = useState([]);
    const [openDonor, setOpenDonor] = useState(false);
    const [openGrant, setOpenGrant] = useState(false);
    const [openInd, setOpenInd] = useState(false);
    const [donorEdit, setDonorEdit] = useState(null);
    const [indEdit, setIndEdit] = useState(null);
    const [donorForm, setDonorForm] = useState({ name: '', country: '', contact_email: '' });
    const [grantForm, setGrantForm] = useState({ donor_id: '', project_id: '', reference: '', amount: '', currency: 'EUR', start_date: '', end_date: '' });
    const [indForm, setIndForm] = useState({ project_id: '', label: '', target_value: '', current_value: '', unit: '', category: '' });
    // Tranches de décaissement
    const [trancheGrant, setTrancheGrant] = useState(null);
    const [tranches, setTranches] = useState(null);
    const [tranchesUnavailable, setTranchesUnavailable] = useState(false);
    const [trForm, setTrForm] = useState({ label: '', amount: '', due_date: '', status: 'prevue' });
    const [trBusy, setTrBusy] = useState(false);

    const elevated = isElevatedOrAdmin({ profile, currentWorkspace });

    const load = async () => {
        if (!currentWorkspace) return;
        const wid = currentWorkspace.id;
        const [{ data: d }, { data: g }, { data: i }, { data: pr }] = await Promise.all([
            supabase.from('donors').select('*').eq('workspace_id', wid).order('created_at', { ascending: false }),
            supabase.from('grants').select('*,donors:donor_id(name),projects:project_id(name)').eq('workspace_id', wid).order('created_at', { ascending: false }),
            supabase.from('impact_indicators').select('*,projects:project_id(name)').eq('workspace_id', wid),
            supabase.from('projects').select('id,name').eq('workspace_id', wid).is('deleted_at', null).order('name'),
        ]);
        setDonors(d || []); setGrants(g || []); setIndicators(i || []); setProjects(pr || []);
    };
    useEffect(() => { if (!authLoading && user) load(); /* eslint-disable-next-line */ }, [authLoading, user, currentWorkspace]);

    const audit = (action, entity_type, entity_id, old_value = null, new_value = null) =>
        supabase.from('audit_logs').insert({ workspace_id: currentWorkspace.id, user_id: user?.id, action, entity_type, entity_id, old_value, new_value });

    // ---- Subventions ----
    const removeGrant = async (g) => {
        if (!window.confirm(`Supprimer la subvention « ${g.reference || g.donors?.name || g.id.slice(0, 8)} » ?`)) return;
        const { error } = await supabase.from('grants').delete().eq('id', g.id);
        if (error) { toast.error("Vous n'êtes pas autorisé·e à supprimer cette subvention."); return; }
        await audit('grant.deleted', 'grant', g.id, { reference: g.reference, amount: g.amount });
        toast.success('Subvention supprimée');
        load();
    };
    const addGrant = async (e) => {
        e.preventDefault();
        const payload = {
            workspace_id: currentWorkspace.id,
            donor_id: grantForm.donor_id || null,
            project_id: grantForm.project_id || null,
            reference: grantForm.reference || null,
            amount: Number(grantForm.amount || 0),
            currency: grantForm.currency,
            start_date: grantForm.start_date || null,
            end_date: grantForm.end_date || null,
            created_by: user?.id,
        };
        const { error } = await supabase.from('grants').insert(payload);
        if (error) { toast.error(`Enregistrement impossible : ${error.message}`); return; }
        toast.success('Subvention enregistrée');
        setOpenGrant(false); setGrantForm({ donor_id: '', project_id: '', reference: '', amount: '', currency: 'EUR', start_date: '', end_date: '' }); load();
    };

    // ---- Bailleurs ----
    const openDonorDialog = (d = null) => {
        setDonorEdit(d);
        setDonorForm(d ? { name: d.name || '', country: d.country || '', contact_email: d.contact_email || '' } : { name: '', country: '', contact_email: '' });
        setOpenDonor(true);
    };
    const saveDonor = async (e) => {
        e.preventDefault();
        if (donorEdit) {
            const { error } = await supabase.from('donors').update(donorForm).eq('id', donorEdit.id);
            if (error) { toast.error(`Modification impossible : ${error.message}`); return; }
            await audit('donor.updated', 'donor', donorEdit.id, { name: donorEdit.name }, donorForm);
            toast.success('Bailleur modifié');
        } else {
            const { error } = await supabase.from('donors').insert({ ...donorForm, workspace_id: currentWorkspace.id });
            if (error) { toast.error(`Enregistrement impossible : ${error.message}`); return; }
        }
        setOpenDonor(false); setDonorEdit(null); setDonorForm({ name: '', country: '', contact_email: '' }); load();
    };
    const removeDonor = async (d) => {
        const linked = grants.filter(g => g.donor_id === d.id).length;
        const msg = linked
            ? `Supprimer le bailleur « ${d.name} » ? ${linked} subvention(s) resteront enregistrées mais ne seront plus rattachées à ce bailleur.`
            : `Supprimer le bailleur « ${d.name} » ?`;
        if (!window.confirm(msg)) return;
        const { error } = await supabase.from('donors').delete().eq('id', d.id);
        if (error) { toast.error(`Suppression impossible : ${error.message}`); return; }
        await audit('donor.deleted', 'donor', d.id, { name: d.name });
        toast.success('Bailleur supprimé');
        load();
    };

    // ---- Indicateurs d'impact ----
    const openIndDialog = (i = null) => {
        setIndEdit(i);
        setIndForm(i
            ? { project_id: i.project_id || '', label: i.label || '', target_value: i.target_value ?? '', current_value: i.current_value ?? '', unit: i.unit || '', category: i.category || '' }
            : { project_id: '', label: '', target_value: '', current_value: '', unit: '', category: '' });
        setOpenInd(true);
    };
    const saveInd = async (e) => {
        e.preventDefault();
        const payload = {
            project_id: indForm.project_id || null,
            label: indForm.label,
            target_value: indForm.target_value !== '' ? Number(indForm.target_value) : null,
            current_value: indForm.current_value !== '' ? Number(indForm.current_value) : 0,
            unit: indForm.unit || null,
            category: indForm.category || null,
        };
        if (indEdit) {
            const { error } = await supabase.from('impact_indicators').update(payload).eq('id', indEdit.id);
            if (error) { toast.error(`Modification impossible : ${error.message}`); return; }
            await audit('impact_indicator.updated', 'impact_indicator', indEdit.id, { label: indEdit.label }, payload);
            toast.success('Indicateur modifié');
        } else {
            const { error } = await supabase.from('impact_indicators').insert({ ...payload, workspace_id: currentWorkspace.id });
            if (error) { toast.error(`Enregistrement impossible : ${error.message}`); return; }
        }
        setOpenInd(false); setIndEdit(null); setIndForm({ project_id: '', label: '', target_value: '', current_value: '', unit: '', category: '' }); load();
    };
    const removeInd = async (i) => {
        if (!window.confirm(`Supprimer l'indicateur « ${i.label} » ?`)) return;
        const { error } = await supabase.from('impact_indicators').delete().eq('id', i.id);
        if (error) { toast.error(`Suppression impossible : ${error.message}`); return; }
        await audit('impact_indicator.deleted', 'impact_indicator', i.id, { label: i.label });
        toast.success('Indicateur supprimé');
        load();
    };

    // ---- Tranches de décaissement ----
    const openTranches = async (g) => {
        setTrancheGrant(g); setTranches(null); setTranchesUnavailable(false);
        setTrForm({ label: '', amount: '', due_date: '', status: 'prevue' });
        const { data, error } = await supabase.from('grant_tranches').select('*').eq('grant_id', g.id).order('due_date', { ascending: true });
        if (error) {
            if ((error.code === '42P01') || /grant_tranches/.test(error.message || '')) setTranchesUnavailable(true);
            else toast.error(error.message);
            setTranches([]);
            return;
        }
        setTranches(data || []);
    };
    const addTranche = async (e) => {
        e.preventDefault();
        setTrBusy(true);
        const payload = {
            workspace_id: currentWorkspace.id, grant_id: trancheGrant.id,
            label: trForm.label || null, amount: Number(trForm.amount || 0),
            due_date: trForm.due_date || null, status: trForm.status, created_by: user?.id,
        };
        const { error } = await supabase.from('grant_tranches').insert(payload);
        setTrBusy(false);
        if (error) { toast.error(`Enregistrement impossible : ${error.message}`); return; }
        setTrForm({ label: '', amount: '', due_date: '', status: 'prevue' });
        openTranches(trancheGrant);
    };
    const setTrancheStatus = async (tr, status) => {
        const patch = { status, received_date: status === 'recue' ? new Date().toISOString().slice(0, 10) : null };
        const { error } = await supabase.from('grant_tranches').update(patch).eq('id', tr.id);
        if (error) { toast.error(error.message); return; }
        openTranches(trancheGrant);
    };
    const removeTranche = async (tr) => {
        if (!window.confirm(`Supprimer la tranche « ${tr.label || fmt(tr.amount)} » ?`)) return;
        const { error } = await supabase.from('grant_tranches').delete().eq('id', tr.id);
        if (error) { toast.error(error.message); return; }
        openTranches(trancheGrant);
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;

    const tranchesSum = (tranches || []).reduce((s, x) => s + Number(x.amount || 0), 0);
    const tranchesReceived = (tranches || []).filter(x => x.status === 'recue').reduce((s, x) => s + Number(x.amount || 0), 0);

    return (
        <div className="space-y-8">
            <div>
                <div className="eyebrow mb-2">{t('grants.eyebrow')}</div>
                <h1 className="font-heading text-4xl">{t('grants.title')}</h1>
                <p className="text-foreground/70 mt-3 max-w-3xl">{t('grants.sub')}</p>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
                <div className="card-soft p-6"><div className="flex items-center justify-between"><div className="eyebrow">{t('grants.kpi_donors')}</div><Users size={16} className="text-secondary" /></div><div className="font-heading text-3xl mt-3">{donors.length}</div></div>
                <div className="card-soft p-6"><div className="flex items-center justify-between"><div className="eyebrow">{t('grants.kpi_grants')}</div><Heart size={16} className="text-primary" /></div><div className="font-heading text-3xl mt-3">{grants.length}</div></div>
                <div className="card-soft p-6"><div className="flex items-center justify-between"><div className="eyebrow">{t('grants.kpi_total')}</div><Target size={16} className="text-secondary" /></div><div className="font-heading text-3xl mt-3">{fmt(grants.reduce((s, g) => s + Number(g.amount || 0), 0))}</div></div>
            </div>

            <Tabs defaultValue="grants" className="w-full">
                <TabsList className="bg-surface-muted">
                    <TabsTrigger value="grants" data-testid="grants-tab-grants">{t('grants.tab_grants')}</TabsTrigger>
                    <TabsTrigger value="donors" data-testid="grants-tab-donors">{t('grants.tab_donors')}</TabsTrigger>
                    <TabsTrigger value="impact" data-testid="grants-tab-impact">{t('grants.tab_impact')}</TabsTrigger>
                </TabsList>

                <TabsContent value="grants" className="mt-6">
                    <div className="flex justify-end mb-4">
                        <Button onClick={() => setOpenGrant(true)} className="bg-primary hover:bg-primary-hover" data-testid="grants-add-grant"><Plus size={14} className="mr-1.5" /> {t('grants.new_grant')}</Button>
                    </div>
                    <div className="card-soft overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="px-6 py-3">{t('grants.col_donor')}</th><th className="px-6 py-3">{t('grants.col_project')}</th><th className="px-6 py-3">{t('grants.col_ref')}</th><th className="px-6 py-3 text-right">{t('grants.col_amount')}</th><th className="px-6 py-3">{t('grants.col_period')}</th><th className="px-6 py-3 w-24"></th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {grants.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-foreground/60">{t('grants.empty_grants')}</td></tr>}
                                {grants.map((g) => {
                                    const editable = canEditEntry({ entry: g, userId: user?.id, profile, currentWorkspace });
                                    return (
                                        <tr key={g.id} className="group" data-testid={`grant-row-${g.id}`}>
                                            <td className="px-6 py-3 font-medium">{g.donors?.name || '—'}</td>
                                            <td className="px-6 py-3 text-foreground/80">{g.projects?.name || '—'}</td>
                                            <td className="px-6 py-3 font-mono text-xs">{g.reference || '—'}</td>
                                            <td className="px-6 py-3 text-right font-mono">{fmt(g.amount)} {g.currency}</td>
                                            <td className="px-6 py-3 text-sm">{g.start_date || '—'} → {g.end_date || '—'}</td>
                                            <td className="px-2 py-3 text-right whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => openTranches(g)} title="Tranches de décaissement" data-testid={`grant-tranches-${g.id}`}><Layers size={13} className="text-secondary" /></Button>
                                                {editable && (
                                                    <Button size="sm" variant="ghost" onClick={() => removeGrant(g)} data-testid={`grant-delete-${g.id}`} className="opacity-0 group-hover:opacity-100"><Trash2 size={13} className="text-destructive" /></Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="donors" className="mt-6">
                    <div className="flex justify-end mb-4">
                        <Button onClick={() => openDonorDialog()} className="bg-primary hover:bg-primary-hover" data-testid="grants-add-donor"><Plus size={14} className="mr-1.5" /> {t('grants.new_donor')}</Button>
                    </div>
                    <div className="card-soft overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="px-6 py-3">{t('grants.col_name')}</th><th className="px-6 py-3">{t('grants.col_country')}</th><th className="px-6 py-3">{t('grants.col_contact')}</th><th className="px-6 py-3 w-24"></th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {donors.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-foreground/60">{t('grants.empty_donors')}</td></tr>}
                                {donors.map(d => (
                                    <tr key={d.id} className="group" data-testid={`donor-row-${d.id}`}>
                                        <td className="px-6 py-3 font-medium">{d.name}</td>
                                        <td className="px-6 py-3 text-foreground/80">{d.country || '—'}</td>
                                        <td className="px-6 py-3 text-sm">{d.contact_email || '—'}</td>
                                        <td className="px-2 py-3 text-right whitespace-nowrap">
                                            {elevated && (<>
                                                <Button size="sm" variant="ghost" onClick={() => openDonorDialog(d)} className="opacity-0 group-hover:opacity-100" data-testid={`donor-edit-${d.id}`}><Pencil size={13} /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => removeDonor(d)} className="opacity-0 group-hover:opacity-100" data-testid={`donor-delete-${d.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                            </>)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="impact" className="mt-6">
                    <div className="flex justify-end mb-4">
                        <Button onClick={() => openIndDialog()} className="bg-primary hover:bg-primary-hover" data-testid="grants-add-indicator"><Plus size={14} className="mr-1.5" /> {t('grants.new_indicator')}</Button>
                    </div>
                    <div className="card-soft overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="px-6 py-3">{t('grants.col_indicator')}</th><th className="px-6 py-3">{t('grants.col_project')}</th><th className="px-6 py-3 text-right">{t('grants.col_target')}</th><th className="px-6 py-3 text-right">{t('grants.col_current')}</th><th className="px-6 py-3">{t('grants.col_unit')}</th><th className="px-6 py-3 w-24"></th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {indicators.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-foreground/60">{t('grants.empty_impact')}</td></tr>}
                                {indicators.map(i => {
                                    const pct = i.target_value ? Math.round((Number(i.current_value || 0) / Number(i.target_value)) * 100) : 0;
                                    return (
                                        <tr key={i.id} className="group" data-testid={`indicator-row-${i.id}`}>
                                            <td className="px-6 py-3 font-medium">{i.label}<div className="text-xs text-muted-foreground mt-0.5">{i.category || '—'}</div></td>
                                            <td className="px-6 py-3 text-foreground/80">{i.projects?.name || '—'}</td>
                                            <td className="px-6 py-3 text-right font-mono">{fmt(i.target_value)}</td>
                                            <td className="px-6 py-3 text-right font-mono text-primary">{fmt(i.current_value)} <span className="text-muted-foreground text-xs">({pct}%)</span></td>
                                            <td className="px-6 py-3 text-sm">{i.unit || '—'}</td>
                                            <td className="px-2 py-3 text-right whitespace-nowrap">
                                                {elevated && (<>
                                                    <Button size="sm" variant="ghost" onClick={() => openIndDialog(i)} className="opacity-0 group-hover:opacity-100" data-testid={`indicator-edit-${i.id}`}><Pencil size={13} /></Button>
                                                    <Button size="sm" variant="ghost" onClick={() => removeInd(i)} className="opacity-0 group-hover:opacity-100" data-testid={`indicator-delete-${i.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                                </>)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Dialog bailleur (création + édition) */}
            <Dialog open={openDonor} onOpenChange={(v) => { setOpenDonor(v); if (!v) setDonorEdit(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{donorEdit ? 'Modifier le bailleur' : t('grants.new_donor')}</DialogTitle></DialogHeader>
                    <form onSubmit={saveDonor} className="space-y-4">
                        <div className="space-y-2"><Label>{t('grants.col_name')}</Label><Input required value={donorForm.name} onChange={(e) => setDonorForm({ ...donorForm, name: e.target.value })} data-testid="donor-name" /></div>
                        <div className="space-y-2"><Label>{t('grants.col_country')}</Label><Input value={donorForm.country} onChange={(e) => setDonorForm({ ...donorForm, country: e.target.value })} data-testid="donor-country" /></div>
                        <div className="space-y-2"><Label>{t('grants.col_contact')}</Label><Input type="email" value={donorForm.contact_email} onChange={(e) => setDonorForm({ ...donorForm, contact_email: e.target.value })} data-testid="donor-email" /></div>
                        <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpenDonor(false)}>{t('common.cancel')}</Button><Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="donor-submit">{t('common.save')}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog subvention */}
            <Dialog open={openGrant} onOpenChange={setOpenGrant}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{t('grants.new_grant')}</DialogTitle></DialogHeader>
                    <form onSubmit={addGrant} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>{t('grants.col_donor')}</Label>
                                <select value={grantForm.donor_id} onChange={(e) => setGrantForm({ ...grantForm, donor_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="grant-donor">
                                    <option value="">—</option>{donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2"><Label>{t('grants.col_project')}</Label>
                                <select value={grantForm.project_id} onChange={(e) => setGrantForm({ ...grantForm, project_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="grant-project">
                                    <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-2"><Label>{t('grants.col_ref')}</Label><Input value={grantForm.reference} onChange={(e) => setGrantForm({ ...grantForm, reference: e.target.value })} data-testid="grant-ref" /></div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>{t('grants.col_amount')}</Label><Input type="number" required value={grantForm.amount} onChange={(e) => setGrantForm({ ...grantForm, amount: e.target.value })} data-testid="grant-amount" /></div>
                            <div className="space-y-2"><Label>{t('common.currency')}</Label>
                                <select value={grantForm.currency} onChange={(e) => setGrantForm({ ...grantForm, currency: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                                    <option>EUR</option><option>USD</option><option>XOF</option><option>XAF</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>{t('common.start_date')}</Label><DatePicker value={grantForm.start_date} onChange={(v) => setGrantForm({ ...grantForm, start_date: v })} testId="grant-start" /></div>
                            <div className="space-y-2"><Label>{t('common.end_date')}</Label><DatePicker value={grantForm.end_date} onChange={(v) => setGrantForm({ ...grantForm, end_date: v })} testId="grant-end" /></div>
                        </div>
                        <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpenGrant(false)}>{t('common.cancel')}</Button><Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="grant-submit">{t('common.save')}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog indicateur (création + édition) */}
            <Dialog open={openInd} onOpenChange={(v) => { setOpenInd(v); if (!v) setIndEdit(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{indEdit ? "Modifier l'indicateur" : t('grants.new_indicator')}</DialogTitle></DialogHeader>
                    <form onSubmit={saveInd} className="space-y-4">
                        <div className="space-y-2"><Label>{t('grants.col_project')}</Label>
                            <select value={indForm.project_id} onChange={(e) => setIndForm({ ...indForm, project_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="ind-project">
                                <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2"><Label>{t('grants.col_indicator')}</Label><Input required value={indForm.label} onChange={(e) => setIndForm({ ...indForm, label: e.target.value })} data-testid="ind-label" /></div>
                        <div className="space-y-2"><Label>{t('grants.col_category')}</Label><Input value={indForm.category} onChange={(e) => setIndForm({ ...indForm, category: e.target.value })} placeholder="Santé, Éducation, Eau…" data-testid="ind-category" /></div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="space-y-2"><Label>{t('grants.col_target')}</Label><Input type="number" value={indForm.target_value} onChange={(e) => setIndForm({ ...indForm, target_value: e.target.value })} data-testid="ind-target" /></div>
                            <div className="space-y-2"><Label>{t('grants.col_current')}</Label><Input type="number" value={indForm.current_value} onChange={(e) => setIndForm({ ...indForm, current_value: e.target.value })} data-testid="ind-current" /></div>
                            <div className="space-y-2"><Label>{t('grants.col_unit')}</Label><Input value={indForm.unit} onChange={(e) => setIndForm({ ...indForm, unit: e.target.value })} placeholder="bénéf., kg, %…" data-testid="ind-unit" /></div>
                        </div>
                        <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpenInd(false)}>{t('common.cancel')}</Button><Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="ind-submit">{t('common.save')}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog tranches de décaissement */}
            <Dialog open={!!trancheGrant} onOpenChange={(v) => { if (!v) setTrancheGrant(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            Tranches — {trancheGrant?.reference || trancheGrant?.donors?.name || 'Subvention'}
                        </DialogTitle>
                    </DialogHeader>
                    {tranchesUnavailable ? (
                        <div className="rounded-lg border border-dashed border-border bg-surface-muted/40 p-4 text-sm text-foreground/70" data-testid="tranches-unavailable">
                            Le suivi des tranches nécessite une mise à jour de la base : exécutez le script
                            <code className="mx-1">supabase/schema_v30_grant_tranches.sql</code> dans le SQL Editor Supabase, puis rouvrez cette fenêtre.
                        </div>
                    ) : tranches === null ? (
                        <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
                    ) : (
                        <div className="space-y-4" data-testid="tranches-panel">
                            <div className="flex flex-wrap gap-4 text-sm">
                                <span>Subvention : <strong>{fmt(trancheGrant?.amount)} {trancheGrant?.currency}</strong></span>
                                <span>Planifié : <strong>{fmt(tranchesSum)}</strong></span>
                                <span className="text-primary">Reçu : <strong>{fmt(tranchesReceived)}</strong></span>
                                {trancheGrant && tranchesSum > Number(trancheGrant.amount || 0) && (
                                    <span className="text-destructive text-xs">⚠ Total des tranches supérieur au montant de la subvention</span>
                                )}
                            </div>
                            <div className="rounded-lg border border-border divide-y divide-border max-h-64 overflow-y-auto">
                                {tranches.length === 0 && <div className="px-4 py-6 text-center text-sm text-foreground/60">Aucune tranche — ajoutez la première ci-dessous.</div>}
                                {tranches.map(tr => (
                                    <div key={tr.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`tranche-row-${tr.id}`}>
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{tr.label || 'Tranche'} — <span className="font-mono">{fmt(tr.amount)} {trancheGrant?.currency}</span></div>
                                            <div className="text-xs text-muted-foreground">Échéance : {tr.due_date || '—'}{tr.received_date ? ` · Reçue le ${tr.received_date}` : ''}</div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <select value={tr.status} onChange={(e) => setTrancheStatus(tr, e.target.value)}
                                                className={`h-8 px-2 rounded-md border border-input text-xs ${TRANCHE_STATUS[tr.status]?.cls || ''}`}
                                                data-testid={`tranche-status-${tr.id}`}>
                                                {Object.entries(TRANCHE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                            </select>
                                            <Button size="sm" variant="ghost" onClick={() => removeTranche(tr)} data-testid={`tranche-delete-${tr.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={addTranche} className="grid sm:grid-cols-5 gap-2 items-end">
                                <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Libellé</Label><Input value={trForm.label} onChange={(e) => setTrForm({ ...trForm, label: e.target.value })} placeholder="Tranche 1 — démarrage" data-testid="tranche-label" /></div>
                                <div className="space-y-1"><Label className="text-xs">Montant</Label><Input type="number" required value={trForm.amount} onChange={(e) => setTrForm({ ...trForm, amount: e.target.value })} data-testid="tranche-amount" /></div>
                                <div className="space-y-1"><Label className="text-xs">Échéance</Label><Input type="date" value={trForm.due_date} onChange={(e) => setTrForm({ ...trForm, due_date: e.target.value })} data-testid="tranche-date" /></div>
                                <Button type="submit" disabled={trBusy} className="bg-primary hover:bg-primary-hover" data-testid="tranche-submit">
                                    {trBusy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} className="mr-1" />} Ajouter
                                </Button>
                            </form>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
